import React, { useState } from 'react';
import { WritingSuggestion } from '../../../../types';
import { apiService } from '../../../../services';
import styles from './WritingSuggestions.module.css';

interface WritingSuggestionsProps {
  questionId: string;
  industryId: string;
  roleId: string;
  currentAnswer: string;
  onSuggestionClick?: (suggestion: WritingSuggestion) => void;
}

const WritingSuggestions: React.FC<WritingSuggestionsProps> = ({
  questionId,
  industryId,
  roleId,
  currentAnswer,
  onSuggestionClick
}) => {
  const [suggestions, setSuggestions] = useState<WritingSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getWritingSuggestions(
        questionId,
        industryId,
        roleId,
        currentAnswer
      );
      
      setSuggestions(response.suggestions);
      setIsExpanded(true);
      
    } catch (err) {
      console.error('Error fetching writing suggestions:', err);
      setError('Failed to load writing suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: WritingSuggestion) => {
    if (onSuggestionClick) {
      // Use the example content instead of the content field
      const suggestionWithExampleContent = {
        ...suggestion,
        content: suggestion.example
      };
      onSuggestionClick(suggestionWithExampleContent);
    }
  };

  const toggleExpanded = () => {
    if (!isExpanded && suggestions.length === 0) {
      fetchSuggestions();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button 
            className={styles.toggleButton}
            onClick={toggleExpanded}
            disabled={loading}
          >
            <i className="fas fa-lightbulb"></i>
            AI Writing Assistance
            <i className={`fas fa-spinner fa-spin ${styles.loadingIcon}`}></i>
          </button>
        </div>
        <div className={styles.loading}>
          <p>Generating personalized writing suggestions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button 
            className={styles.toggleButton}
            onClick={toggleExpanded}
          >
            <i className="fas fa-lightbulb"></i>
            AI Writing Assistance
            <i className="fas fa-chevron-down"></i>
          </button>
        </div>
        <div className={styles.error}>
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <button onClick={fetchSuggestions} className={styles.retryButton}>
            <i className="fas fa-redo"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          className={`${styles.toggleButton} ${isExpanded ? styles.expanded : ''}`}
          onClick={toggleExpanded}
        >
          <i className="fas fa-lightbulb"></i>
          AI Writing Assistance
          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
        </button>
        {suggestions.length > 0 && (
          <span className={styles.suggestionCount}>
            {suggestions.length} suggestions
          </span>
        )}
      </div>

      {isExpanded && (
        <div className={styles.suggestionsContainer}>
          {suggestions.length === 0 ? (
            <div className={styles.noSuggestions}>
              <i className="fas fa-info-circle"></i>
              <p>Click the button above to get AI-powered writing suggestions for this question.</p>
            </div>
          ) : (
            <div className={styles.suggestionsList}>
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index} 
                  className={styles.suggestionCard}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className={styles.suggestionHeader}>
                    <h4 className={styles.suggestionTitle}>
                      <i className="fas fa-lightbulb"></i>
                      {suggestion.title}
                    </h4>
                  </div>
                  
                  <div className={styles.suggestionContent}>
                    <p>{suggestion.content}</p>
                  </div>
                  
                  {suggestion.example && (
                    <div className={styles.suggestionExample}>
                      <div className={styles.suggestionExampleHeader}>
                        <strong>Suggestion:</strong>
                        <button 
                          className={styles.useButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSuggestionClick(suggestion);
                          }}
                        >
                          <i className="fas fa-plus"></i>
                          Use This
                        </button>
                      </div>
                      <p>{suggestion.example}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WritingSuggestions;
